import React, { useState } from 'react'
import { Panel } from 'primereact/panel'
import { Divider } from 'primereact/divider'
import { Button } from 'primereact/button'

import { ChargePoint, Connector } from '../../redux/api/web-api'
import useText from '../../hooks/useText'
import Text from '../../../ui/Text'
import { isChargingConnector } from '../../../lib/findChargingConnector'
import { useAdaptConnectorNaming } from '../../../lib/adaptConnectorNaming'

export type ChargePointOverviewWidgetProps = {
    chargePoint: ChargePoint
}

export function ChargePointOverviewWidget({ chargePoint }: ChargePointOverviewWidgetProps): React.ReactElement {
    const text = useText()
    const adaptConnectorNaming = useAdaptConnectorNaming()

    const getConnectorInfo = (chargePoint: ChargePoint, connector: Connector) => {
        const connectorInfo = {
            name: adaptConnectorNaming(connector.name),
            factoryCurrent: connector.factoryCurrent,
            supplyCurrent: chargePoint.supplyCurrent,
            userCurrent: connector.userCurrent || 0,
            installationPhase: connector.installationPhase,
        }

        return <Text code="msgOcppConnectorInfo" values={{ ...connectorInfo }} />
    }
    const [learnMoreText, setLearnMoreText] = useState(false)
    return (
        <Panel
            toggleable
            header={
                <div className="flex gap-2 align-items-center">
                    <span data-testid="charger-overview-title">{text('lblChargerOverview')}</span>

                    <Button
                        text
                        severity="info"
                        onClick={() => setLearnMoreText(!learnMoreText)}
                        data-testid="charger-overview-toggle-learn-more"
                    >
                        {learnMoreText ? text('btnShowLess') : text('btnShowMore')}
                    </Button>
                </div>
            }
            data-testid="charge-point-overview-widget"
        >
            {learnMoreText && (
                <>
                    {chargePoint.connectors?.filter(isChargingConnector)?.map((connector) => (
                        <div
                            className="text-700"
                            key={connector.connectorId}
                            data-testid={`charger-overview-connector-info-${connector.connectorId}`}
                        >
                            {getConnectorInfo(chargePoint, connector)}
                        </div>
                    ))}
                    <Divider />
                </>
            )}
            <div
                className="flex justify-content-between mb-3"
                data-testid="charger-overview-charging-efficiency-service-row"
            >
                <span data-testid="charger-overview-charging-efficiency-service-label">{text('lblOcppProxy')}</span>
                {chargePoint?.ocppEnabled ? (
                    <i
                        className="pi pi-check text-green-300 font-bold"
                        data-testid="charger-overview-charging-efficiency-service-icon-enabled"
                    ></i>
                ) : (
                    <i
                        className="pi pi-times text-gray-500 font-bold"
                        title={text('lblDisabledStatus')}
                        data-testid="charger-overview-charging-efficiency-service-icon-disabled"
                    ></i>
                )}
            </div>
            <div className="flex justify-content-between mb-3" data-testid="charger-overview-loadbalanced-row">
                <span data-testid="charger-overview-loadbalanced-label">
                    <Text code="lbLoadbalancerEnabled"></Text>
                </span>
                {chargePoint?.loadBalancerGroupId && chargePoint.inActiveLoadBalancerGroup ? (
                    <i
                        className="pi pi-check text-green-300 font-bold"
                        title={text('lblChargerIsLoadbalanced')}
                        data-testid="charger-overview-loadbalanced-icon-enabled"
                    ></i>
                ) : (
                    <i
                        className="pi pi-times text-gray-500 font-bold"
                        title={text('lblChargerIsNotLoadbalanced')}
                        data-testid="charger-overview-loadbalanced-icon-disabled"
                    ></i>
                )}
            </div>
            <div className="flex justify-content-between" data-testid="charger-overview-weekly-schedule-row">
                <span data-testid="charger-overview-weekly-schedule-label">
                    <Text code="lblWeeklyScheduleIsSet"></Text>
                </span>
                <span className="flex flex-column">
                    {chargePoint.connectors?.filter(isChargingConnector)?.map((connector) => {
                        const getConnectorScheduleIcon = () => {
                            if (connector?.hasSchedule === null) {
                                return (
                                    <i
                                        className="pi pi-question text-gray-600 font-bold"
                                        title={text('lblUnknown')}
                                        data-testid={`charger-overview-weekly-schedule-icon-unknown-${connector.connectorId}`}
                                    />
                                )
                            }
                            if (connector?.hasSchedule) {
                                return (
                                    <i
                                        className="pi pi-check text-green-300 font-bold"
                                        title={text('lblChargerHasSchedules', { connectorName: connector?.name })}
                                        data-testid={`charger-overview-weekly-schedule-icon-enabled-${connector.connectorId}`}
                                    />
                                )
                            }
                            return (
                                <i
                                    className="pi pi-times text-gray-500 font-bold"
                                    title={text('msgNoSchedules', { connectorName: connector?.name })}
                                    data-testid={`charger-overview-weekly-schedule-icon-disabled-${connector.connectorId}`}
                                />
                            )
                        }

                        return (
                            <span
                                className="mb-1"
                                key={connector.connectorId}
                                data-testid={`charger-overview-weekly-schedule-connector-${connector.connectorId}`}
                            >
                                {getConnectorScheduleIcon()}
                            </span>
                        )
                    })}
                </span>
            </div>
        </Panel>
    )
}
